import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Formik,
  Form,
  FieldArray,
  FieldArrayRenderProps,
  FormikProps,
} from "formik";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Grid,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  MultipleExpenseFormData,
  OtherExpenseEntry,
  EXPENSE_CATEGORIES,
  getEmptyOtherExpenseEntry,
  OtherExpense,
  PaymentDetail,
} from "./types";
import { otherExpenseApi } from "./apiService";
import { useToast } from "../../components/Toast";
import CustomExpenseTypeDialog from "./components/CustomExpenseTypeDialog";
import { Loading } from "../../components/Loading";
import ExpenseForm from "./components/ExpenseForm";
import DeletedExpenseDetails from "../rentalBooking/components/DeletedExpenseDetails";
import { formatCurrency } from "../../utils/utils";
import { multipleExpenseValidationSchema } from "./validationSchema";

const AddOtherExpense = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customTypeDialogOpen, setCustomTypeDialogOpen] = useState(false);
  const [pendingArrayHelpers, setPendingArrayHelpers] = useState<any>(null);
  const [expenseData, setExpenseData] = useState<MultipleExpenseFormData>({
    expenses: [],
  });
  const [deletedExpenses, setDeletedExpenses] = useState<OtherExpense[]>([]);
  const { date = null } = useParams();
  const [showChangeLogs, setShowChangeLogs] = useState(false);

  const fetchExpenses = async () => {
    setIsLoading(true);
    let promises = [];
    promises.push(otherExpenseApi.getExpensesByExpenseDate(date!));
    promises.push(otherExpenseApi.getDeletedExpenses());
    const [expenses, deletedExpenses] = await Promise.all(promises);
    const expensesData = expenses.data.map((expense: OtherExpense) => ({
      ...expense,
      isEdited: false,
      isDeleted: false,
      isNew: false,
      payments: expense.payments.map((payment: PaymentDetail) => ({
        ...payment,
        isEdited: false,
        isDeleted: false,
        isNew: false,
      })),
    }));
    setDeletedExpenses(deletedExpenses);
    setExpenseData({ expenses: expensesData });
    setIsLoading(false);
  };

  useEffect(() => {
    if (date) {
      fetchExpenses();
    }
  }, []);

  const handleSubmit = async (values: MultipleExpenseFormData) => {
    try {
      setSubmitting(true);
      if (date === null) {
        const res = await otherExpenseApi.createExpense(values.expenses);
        if (res) {
          showToast("Expenses saved successfully", "success");
          navigate("/other-expense");
        } else {
          showToast("Failed to save expenses", "error");
        }
      } else {
        let promises = [];
        const expensesToCreate = values.expenses.filter(
          (expense) => expense.isNew
        );
        const valuesToUpdate = values.expenses.filter(
          (expense) => expense.isEdited && !expense.isDeleted && !expense.isNew
        );

        const expensesToChangedDelete = valuesToUpdate
          .map((changed) =>
            expenseData.expenses.find((initial) => initial.id === changed.id)
          )
          .filter(Boolean) // Filters out undefined/null
          .map((expense) => ({
            ...expense,
            isEdited: true,
          })) as OtherExpenseEntry[];

        const expensesToDelete = values.expenses.filter(
          (expense) => expense.isDeleted
        );

        const paymentToCreate = values.expenses
          .filter((expense) =>
            expense.payments.some((payment) => payment.isNew)
          )
          .flatMap((expense) => expense.payments)
          .filter((payment) => payment.isNew);

        const paymentsFlatMapped = values.expenses.flatMap(
          (expense) => expense.payments
        );

        const paymentToUpdate = paymentsFlatMapped.filter(
          (payment) => payment.isEdited && !payment.isNew && !payment.isDeleted
        );

        const paymentToDelete = paymentsFlatMapped.filter(
          (payment) => payment.isDeleted
        );

        const paymentToChangedDelete = values.expenses
          .flatMap((expense) => expense.payments)
          .filter(
            (payment) =>
              !payment.isNew && !payment.isDeleted && payment.isEdited
          ) as PaymentDetail[];

        if (valuesToUpdate.length > 0) {
          promises.push(otherExpenseApi.bulkUpdateExpense(valuesToUpdate));
        }
        if (expensesToChangedDelete.length > 0) {
          promises.push(
            otherExpenseApi.bulkDeleteExpense(expensesToChangedDelete)
          );
        }
        if (expensesToDelete.length > 0) {
          promises.push(otherExpenseApi.bulkDeleteExpense(expensesToDelete));
        }
        if (expensesToCreate.length > 0) {
          promises.push(otherExpenseApi.createExpense(expensesToCreate));
        }
        if (paymentToCreate.length > 0) {
          promises.push(otherExpenseApi.createPayment(paymentToCreate));
        }
        if (paymentToUpdate.length > 0) {
          promises.push(otherExpenseApi.bulkUpdatePayment(paymentToUpdate));
        }
        if (paymentToDelete.length > 0) {
          promises.push(otherExpenseApi.bulkDeletePayment(paymentToDelete));
        }
        if (paymentToChangedDelete.length > 0) {
          promises.push(
            otherExpenseApi.bulkDeletePayment(paymentToChangedDelete)
          );
        }
        const res = await Promise.all(promises);
        showToast("Expenses updated successfully", "success");
        navigate("/other-expense");
      }
    } catch (error) {
      console.error("Error creating expenses:", error);
      showToast("Failed to save expenses", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate("/other-expense");
  };

  const handleAddExpenseType = (type: string, arrayHelpers: any) => {
    if (type === "Other") {
      // Show dialog to get custom expense type
      setPendingArrayHelpers(arrayHelpers);
      setCustomTypeDialogOpen(true);
    } else {
      // Add expense directly
      arrayHelpers.push(getEmptyOtherExpenseEntry(type));
    }
  };

  const handleCustomTypeConfirm = (customType: string) => {
    if (pendingArrayHelpers) {
      const newExpense = getEmptyOtherExpenseEntry("Other");
      newExpense.category = customType;
      pendingArrayHelpers.push(newExpense);
      setPendingArrayHelpers(null);
    }
    setCustomTypeDialogOpen(false);
  };

  const handleCustomTypeCancel = () => {
    setPendingArrayHelpers(null);
    setCustomTypeDialogOpen(false);
  };

  const renderExpenseTypeSelector = (
    arrayHelpers: FieldArrayRenderProps,
    expenses: OtherExpenseEntry[]
  ) => {
    const existingTypes = new Set(
      expenses.map((expense) => expense.expenseType)
    );
    const availableTypes = EXPENSE_CATEGORIES.filter(
      (type) => !existingTypes.has(type)
    );

    if (expenses.length === 0) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            borderRadius: 1,
            position: "sticky",
            top: 16,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.75rem" }}>
            Select expense type to add:
          </Typography>
          <Stack direction="column" spacing={0.5}>
            {EXPENSE_CATEGORIES.map((type) => (
              <Chip
                key={type}
                label={type}
                onClick={() => handleAddExpenseType(type, arrayHelpers)}
                color="primary"
                variant="outlined"
                icon={<AddIcon sx={{ fontSize: "1rem" }} />}
                sx={{
                  width: "100%",
                  justifyContent: "flex-start",
                  height: 32,
                  "& .MuiChip-label": {
                    flex: 1,
                    textAlign: "left",
                    pl: 1,
                    fontSize: "0.75rem",
                  },
                }}
              />
            ))}
          </Stack>
        </Paper>
      );
    }

    if (availableTypes.length === 0) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            borderRadius: 1,
            position: "sticky",
            top: 16,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.75rem" }}>
            All expense types added
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            Use the "Add Another" button in each section to add more expenses of
            the same type.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        elevation={3}
        sx={{
          p: 1.5,
          borderRadius: 1,
          position: "sticky",
          top: 16,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.75rem" }}>
          Select expense type to add:
        </Typography>
        <Stack direction="column" spacing={0.5}>
          {availableTypes.map((type) => (
            <Chip
              key={type}
              label={type}
              onClick={() => handleAddExpenseType(type, arrayHelpers)}
              color="primary"
              variant="outlined"
              icon={<AddIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                height: 32,
                "& .MuiChip-label": {
                  flex: 1,
                  textAlign: "left",
                  pl: 1,
                  fontSize: "0.75rem",
                },
              }}
            />
          ))}
        </Stack>
      </Paper>
    );
  };

  const renderExpensesByType = (
    type: string,
    expenses: OtherExpenseEntry[],
    arrayHelpers: FieldArrayRenderProps,
    formikProps: FormikProps<MultipleExpenseFormData>
  ) => {
    const expensesOfType = expenses
      .map((expense, index) => ({ expense, index }))
      .filter(
        ({ expense }) => expense.expenseType === type && !expense.isDeleted
      );

    if (expensesOfType.length === 0) return null;

    return (
      <Box key={type} sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ mb: 1, fontSize: "1rem", fontWeight: "bold" }}
        >
          {type}
        </Typography>
        {expensesOfType.map(({ expense, index }, typeIndex) => (
          <ExpenseForm
            key={index}
            type={type}
            index={index}
            typeIndex={typeIndex}
            onDelete={() =>
              formikProps.setFieldValue(`expenses.${index}.isDeleted`, true)
            }
            formikProps={formikProps}
          />
        ))}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleAddExpenseType(type, arrayHelpers)}
          sx={{ mb: 1 }}
        >
          Add Another {type}
        </Button>
        <Divider sx={{ my: 2 }} />
        {deletedExpenses?.length > 0 && (
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "gray",
                fontSize: "0.75rem",
              }}
              onClick={() => {
                setShowChangeLogs(!showChangeLogs);
              }}
            >
              Expense Change Logs
              {showChangeLogs ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </Typography>
            {showChangeLogs && (
              <>
                <DeletedExpenseDetails
                  deletedExpenseDetails={deletedExpenses}
                />
              </>
            )}
          </Grid>
        )}
      </Box>
    );
  };

  const calculateTotals = (expenses: OtherExpenseEntry[]) => {
    const activeExpenses = expenses.filter((expense) => !expense.isDeleted);
    const totalAmount = activeExpenses.reduce((sum, expense) => {
      return sum + (Number(expense.amount) || 0);
    }, 0);
    const totalPaid = activeExpenses.reduce((sum, expense) => {
      return sum + (Number(expense.amount) || 0);
    }, 0);
    const balance = totalAmount - totalPaid;

    return { totalAmount, totalPaid, balance };
  };

  if (submitting || isLoading) {
    return submitting ? (
      <Loading message="Saving expenses..." fullScreen={true} />
    ) : (
      <Loading message="Loading expenses..." fullScreen={true} />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2 }}
        >
          Back to Expenses
        </Button>
      </Box>
      <Paper
        sx={{
          p: 2,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontSize={"1rem"}>
          Other Expenses
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {/* {formatCurrency(totalAmount)} */}
        </Typography>
      </Paper>

      <Formik
        initialValues={expenseData}
        validationSchema={multipleExpenseValidationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          const { values, isValid } = formikProps;
          const { totalAmount, totalPaid, balance } = calculateTotals(
            values.expenses
          );
          const activeExpenses = values.expenses.filter(
            (expense) => !expense.isDeleted
          );

          return (
            <Form>
              <Grid container spacing={2}>
                {/* Main Content */}
                <Grid item xs={12} lg={9}>
                  <FieldArray name="expenses">
                    {(arrayHelpers) => (
                      <Box>
                        {EXPENSE_CATEGORIES.map((type) =>
                          renderExpensesByType(
                            type,
                            values.expenses,
                            arrayHelpers,
                            formikProps
                          )
                        )}

                        {/* Payment Details Summary */}
                        {/* {activeExpenses.length > 0 && (
                          <Paper elevation={3} sx={{ p: 2, mt: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Payment Details
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Balance:
                                </Typography>
                                <Typography
                                  variant="h6"
                                  color={
                                    balance >= 0 ? "success.main" : "error.main"
                                  }
                                >
                                  {formatCurrency(balance)}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Total Paid:
                                </Typography>
                                <Typography variant="h6">
                                  {formatCurrency(totalPaid)}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Total Amount:
                                </Typography>
                                <Typography variant="h6">
                                  ₹{totalAmount.toFixed(2)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                        )} */}
                      </Box>
                    )}
                  </FieldArray>
                </Grid>

                {/* Sidebar */}
                <Grid item xs={12} lg={3}>
                  <FieldArray name="expenses">
                    {(arrayHelpers) =>
                      renderExpenseTypeSelector(arrayHelpers, values.expenses)
                    }
                  </FieldArray>
                </Grid>
              </Grid>

              {/* Submit Buttons */}
              {activeExpenses.length > 0 && (
                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{
                      fontSize: "0.8rem",
                      height: "30px",
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      submitting ? <CircularProgress size={20} /> : <SaveIcon />
                    }
                    sx={{
                      fontSize: "0.8rem",
                      height: "30px",
                    }}
                    disabled={
                      !isValid || activeExpenses.length === 0 || submitting
                    }
                  >
                    {submitting ? "Saving..." : "Save Expenses"}
                  </Button>
                </Box>
              )}
            </Form>
          );
        }}
      </Formik>

      {/* Custom Type Dialog */}
      <CustomExpenseTypeDialog
        open={customTypeDialogOpen}
        onClose={handleCustomTypeCancel}
        onConfirm={handleCustomTypeConfirm}
      />
    </Container>
  );
};

export default AddOtherExpense;
